import { CoreLogger } from '../../logger/core-logger';
import { JSONObject, JSONValue } from '../../types';

export async function fetchConfigurationData<T = JSONValue>(
  url: string,
  accessToken?: string,
): Promise<T | null> {
  if (!url) {
    CoreLogger.loggerManager.error('no error specified');
    return null;
  }
  const headers = new Headers();
  if (accessToken) {
    headers.append('Authorization', `Bearer ${accessToken}`);
  }

  try {
    const response = await fetch(url, {
      headers: accessToken ? headers : undefined,
    });
    return (await response.json()) as T;
  } catch (error) {
    CoreLogger.loggerManager.error(
      'Failed to fetch configuration data:',
      error,
    );
    return null;
  }
}

export const transformData = (
  data: JSONValue,
  callback: <T>(val: T) => T,
): JSONValue => {
  if (Array.isArray(data)) {
    return data.map((item) => transformData(item, callback));
  }

  if (data !== null && typeof data === 'object') {
    const newObj: JSONObject = {};
    for (const [key, value] of Object.entries(data)) {
      newObj[key] = transformData(value, callback);
    }
    return newObj;
  }

  return callback(data);
};

// cyrb53 (c) 2018 bryc (github.com/bryc). License: Public domain. Attribution appreciated.
// A fast and simple 64-bit (or 53-bit) string hash function with decent collision resistance.
// Largely inspired by MurmurHash2/3, but with a focus on speed/simplicity.
// See https://stackoverflow.com/questions/7616461/generate-a-hash-from-string-in-javascript/52171480#52171480
// https://github.com/bryc/code/blob/master/jshash/experimental/cyrb53.js
// mix of the above and
// https://github.com/mbaersch/cyrb53-hasher/tree/main
export const cyrb53 = (str: string, seed = 0) => {
  let h1 = 0xdeadbeef ^ seed,
    h2 = 0x41c6ce57 ^ seed;
  for (let i = 0, ch; i < str.length; i++) {
    ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);

  return (
    (h2 >>> 0).toString(16).padStart(8, '0') +
    (h1 >>> 0).toString(16).padStart(8, '0')
  );
};
